import { useEffect, useState } from 'react';
import type { UtmParams, UtmKey } from '../types';

const UTM_KEYS: UtmKey[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

const STORAGE_KEY = 'hit_utm_data';
const STORAGE_TIMESTAMP = 'hit_utm_timestamp';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

export function useUtmTracking() {
  const [utmParams, setUtmParams] = useState<UtmParams>({});

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const newUtms: UtmParams = {};
    let hasNewUtms = false;

    UTM_KEYS.forEach(key => {
      const value = urlParams.get(key);
      if (value) {
        newUtms[key] = value;
        hasNewUtms = true;
      }
    });

    if (hasNewUtms) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newUtms));
      localStorage.setItem(STORAGE_TIMESTAMP, String(Date.now()));
      setUtmParams(newUtms);
    } else {
      const stored = localStorage.getItem(STORAGE_KEY);
      const storedTime = localStorage.getItem(STORAGE_TIMESTAMP);

      if (stored && storedTime && Date.now() - Number(storedTime) < TTL_MS) {
        setUtmParams(JSON.parse(stored) as UtmParams);
      }
    }

    // Carril del embudo VSL (?p=) y variante del gancho (?v=). NO son UTMs, pero
    // llegan en la misma URL desde el vídeo y hay que CONSERVARLOS igual para poder
    // leer coste/agenda × cierre POR CARRIL. Se guardan aparte (hit_carril_data) y
    // los reenvía lib/webhook.ts al enviar la aplicación. Solo se escriben si vienen
    // en la URL, así una recarga sin parámetros no borra el carril ya guardado.
    try {
      const carril: Record<string, string> = {};
      const p = urlParams.get('p');
      const v = urlParams.get('v');
      if (p) carril.p = p;
      if (v) carril.v = v;
      if (carril.p || carril.v) {
        localStorage.setItem('hit_carril_data', JSON.stringify(carril));
      }
    } catch {
      /* fail-open: la atribución por carril es best-effort, nunca rompe la página */
    }
  }, []);

  const appendUtmsToUrl = (url: string): string => {
    if (Object.keys(utmParams).length === 0) return url;

    const urlObj = new URL(url, window.location.origin);
    (Object.entries(utmParams) as [string, string][]).forEach(([key, value]) => {
      if (value) urlObj.searchParams.set(key, value);
    });
    return urlObj.toString();
  };

  return { utmParams, appendUtmsToUrl };
}
